// public-reader/components/ForkAction.tsx — "Continue from this chapter"
// button on the reader.
//
// Three states:
//   1. Unauthed visitor → button label "Continue from chapter N →";
//      onClick navigates to www.emberhollow.ai/?fork=<slug>&chapter=<N>
//      so the auth flow completes in the player surface, then the
//      player surface picks up the params and runs the fork after
//      signin.
//   2. Authed visitor → button label "Continue from chapter N";
//      onClick POSTs the fork directly, then navigates to www with the
//      newly minted storyId so the player surface loads + activates it.
//   3. Author's own book → label "Continue this story →"; behaves like
//      the authed path but doesn't write a referral. (The route's
//      attributeForkReferral helper already skips self-forks; this
//      branch just changes the copy.)
//
// `allowContinuation = false` greys out the button with a tooltip.

import { useCallback, useState, type CSSProperties } from "react";
import { getAccessToken, getUser } from "../../auth/tokens";
import { forkPublicBook, PublicBooksApiError } from "../services/publicBooksApi";

const WWW_URL = "https://www.emberhollow.ai";

export interface ForkActionProps {
  slug: string;
  chapterNumber: number;
  /** Whether the book's author has enabled continuations. */
  allowContinuation: boolean;
  /** id of the original author — used to detect "this is my own book". */
  authorUserId: string;
  accent: string;
  accentMuted: string;
  ink: string;
  inkMuted: string;
  paperEdge: string;
}

export default function ForkAction(props: ForkActionProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = typeof window !== "undefined" ? getAccessToken() : null;
  const user = typeof window !== "undefined" ? getUser() : null;
  const isOwnBook = Boolean(user && user.id === props.authorUserId);

  const baseLabel = isOwnBook
    ? `Continue this story from chapter ${props.chapterNumber}`
    : `Continue from chapter ${props.chapterNumber}`;

  const handleClick = useCallback(async () => {
    if (!props.allowContinuation) return;

    // Unauthed: redirect to www with the fork param. The player surface
    // (App.tsx) detects ?fork=<slug>&chapter=<N> on post-auth and POSTs
    // the fork itself.
    if (!token) {
      const target =
        `${WWW_URL}/?fork=${encodeURIComponent(props.slug)}&chapter=${props.chapterNumber}`;
      window.location.href = target;
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await forkPublicBook(props.slug, props.chapterNumber);
      // Land the user in the player UI for the new story. ?activeStoryId
      // tells App.tsx which row to load + activate; a future commit can
      // also drop a toast about who they forked from.
      window.location.href = `${WWW_URL}/?activeStoryId=${encodeURIComponent(res.newStoryId)}&forkFromSlug=${encodeURIComponent(props.slug)}&forkFromChapter=${props.chapterNumber}`;
    } catch (err) {
      const msg =
        err instanceof PublicBooksApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Fork failed.";
      setError(msg);
      setBusy(false);
    }
  }, [props.allowContinuation, props.chapterNumber, props.slug, token]);

  // Styles
  const btn: CSSProperties = {
    display: "inline-block",
    padding: "8px 16px",
    background: "transparent",
    color: props.allowContinuation ? props.accent : props.inkMuted,
    border: `1px solid ${props.allowContinuation ? props.accentMuted : props.paperEdge}`,
    cursor: props.allowContinuation ? (busy ? "wait" : "pointer") : "not-allowed",
    fontFamily: "inherit",
    fontSize: "0.85rem",
    borderRadius: 2,
    transition: "background 0.18s",
    opacity: busy ? 0.65 : 1,
  };
  const errStyle: CSSProperties = {
    color: "#c47a5a",
    fontSize: "0.8rem",
    marginTop: 6,
  };

  if (!props.allowContinuation) {
    return (
      <button
        type="button"
        style={btn}
        disabled
        title="The author has disabled continuations for this book."
      >
        Continuations disabled
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        style={btn}
        onClick={handleClick}
        disabled={busy}
        aria-label={baseLabel}
      >
        {busy ? "Forking…" : `${baseLabel} →`}
      </button>
      {error && <div style={errStyle}>{error}</div>}
    </div>
  );
}
